import React, { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
  Alert,
} from "reactstrap";
import { EmployeeData } from "../EmployeeProfile";
import { IsValidPermissions } from "../../../Routes/AuthProtected";
import { useDeleteEmployeeByUUIDMutation } from "slices/employee/employeeSlice";

interface ArchiveMembersProps {
  employeeProfileData: EmployeeData | null;
  IS_ACTIVE: boolean;
}

export const ArchiveMembers: React.FC<ArchiveMembersProps> = ({
  employeeProfileData,
  IS_ACTIVE,
}) => {
  const skillsString = employeeProfileData?.skills || "";
  const skillsArray = skillsString ? skillsString.split(",") : [];
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [textToConfirm, setTextToConfirm] = useState("ARCHIVE");
  const [endContractNotes, setEndContractNotes] = useState("");
  const [endContractDate, setEndContractDate] = useState("");

  const [deleteEmployeeByUUID] = useDeleteEmployeeByUUIDMutation();

  const toggleDeleteModal = () => {
    setDeleteModalOpen(!deleteModalOpen);
    setConfirmationText("");
    setEndContractNotes("");
    setEndContractDate("");
  };

  const handleDeleteProject = async () => {
    if (
      confirmationText === textToConfirm &&
      employeeProfileData?.employee_uuid
    ) {
      try {
        await deleteEmployeeByUUID({
          data: {
            employee_uuid: employeeProfileData.employee_uuid,
            notes: endContractNotes,
            end_contract_date: endContractDate,
          },
        }).unwrap();
        toggleDeleteModal();
      } catch (error) {
        console.error("Failed to delete employee:", error);
      }
    }
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="d-flex align-items-center">
          <i className="ri-user-fill fs-4"></i>
          <h4 className="card-title flex-grow-1 mb-0">Archive Member</h4>
        </CardHeader>
        <CardBody>
          <div className="table-card">
            {!IS_ACTIVE ? (
              <Alert color="warning" className="text-center">
                <i className="ri-alarm-warning-line fs-4"></i> This employee is
                already archived.
              </Alert>
            ) : (
              <div className="d-flex align-items-center justify-content-center text-center">
                <div>
                  <p>
                    This action will archive the member, and permits or reports
                    cannot be updated by the member after this action.
                  </p>
                  <Button
                    color="danger"
                    onClick={toggleDeleteModal}
                    className="btn-md shadow-none mb-2"
                  >
                    <i className="ri-trash-fill text-white fs-4">
                      Delete Member
                    </i>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>
          Are you sure you want to archive this member{" "}
          <b>{employeeProfileData?.employee_fullname}</b>?
        </ModalHeader>
        <ModalBody>
          <p>
            You are about to archive this member. This action cannot be undone.
          </p>
          <p>
            Enter the following text to confirm: <b>{textToConfirm}</b>
          </p>
          <Input
            type="text"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            className="mb-3"
          />

          <FormGroup>
            <Label for="archive until this date">Archive until this date</Label>
            <Input
              type="date"
              id="endContractDate"
              value={endContractDate}
              onChange={(e) => setEndContractDate(e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label for="archive comments">Archive Comments</Label>
            <Input
              type="textarea"
              id="endContractNotes"
              value={endContractNotes}
              onChange={(e) => setEndContractNotes(e.target.value)}
              placeholder="Enter reason for termination/end of contract"
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel, keep member
          </Button>
          <Button
            color="danger"
            onClick={handleDeleteProject}
            disabled={confirmationText !== textToConfirm || !endContractNotes}
          >
            Yes, delete member
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};
